import React from 'react'
import { KYCDocumentsPresenterProps, KYCUploadedDocument } from '../../lib/types'
import {
  Card,
  CheckCircleIcon,
  DownloadIcon,
  Flex,
  P,
  ReportIcon,
  Span,
} from '../../lib/components'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'

const KYCDocumentsPresenter = ({
  kycDocumentsChecklist,
  handleWelcomeLetterDownload,
  handleClientAgreementDownload,
  handleSumsubKYCDocumentDownload,
}: KYCDocumentsPresenterProps) => {
  const { breakpoint } = useBreakpoint()

  const getKYCDocument = (documentList: KYCUploadedDocument[], documentName: string) => {
    const document = documentList.find((doc) => doc.name === documentName)
    return document
  }

  return (
    <Card classes={[breakpoint && breakpoint > SM ? 'p-40' : 'p-24']}>
      <P text='Steps and Documents Checklist' classes={['mb-16']} />
      <Flex variant='space-between'>
        <Flex>
          {kycDocumentsChecklist.isEmailVerified ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-email-verified-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-email-unverified-icon'
            />
          )}

          <Span variant='bold' text='Email Verified' classes={['ml-16']} />
        </Flex>
      </Flex>

      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.isNdaAccepted ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-nda-accepted-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-nda-not-accepted-icon'
            />
          )}
          <Span variant='bold' text='Confidentiality Agreement Accepted' classes={['ml-16']} />
        </Flex>
      </Flex>

      {/* REMOVED: Self Declaration Agreement - now clubbed with Client Agreement */}

      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.kycStatus === 'APPROVED' ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-kyc-complete-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-kyc-incomplete-icon'
            />
          )}
          <Span variant='bold' text='KYC Complete' classes={['ml-16']} />
        </Flex>
      </Flex>

      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.isInvestmentEssentialsAgreementAccepted ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-risk-checklist-accepted-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-risk-checklist-not-accepted-icon'
            />
          )}

          <Span variant='bold' text='Risk Checklist Accepted' classes={['ml-16']} />
        </Flex>
      </Flex>

      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.isTobAccepted ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-terms-of-business-accepted-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-terms-of-business-not-accepted-icon'
            />
          )}

          <Span variant='bold' text='Terms of Business Accepted' classes={['ml-16']} />
        </Flex>
      </Flex>

      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.isTransactionChecklistAgreementAccepted ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-transaction-checklist-accepted-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-transaction-checklist-not-accepted-icon'
            />
          )}

          <Span variant='bold' text='Transaction Checklist Accepted' classes={['ml-16']} />
        </Flex>
      </Flex>

      {/* Downloadable Documents Section */}
      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.welcomeLetter ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-welcome-letter-shared-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-welcome-letter-not-shared-icon'
            />
          )}
          <Span variant='bold' text='Welcome Letter Shared' classes={['ml-16']} />
        </Flex>

        {kycDocumentsChecklist.welcomeLetter && (
          <DownloadIcon
            height='18'
            width='18'
            classes={['pointer', 'mt-asuto']}
            onClick={() =>
              handleWelcomeLetterDownload(kycDocumentsChecklist.welcomeLetter as string)
            }
            dataTestId='kyc-documents-welcome-letter-download-icon'
          />
        )}
      </Flex>

      <Flex variant='space-between' classes={['mt-4']}>
        <Flex>
          {kycDocumentsChecklist.clientAgreementDocumentId ? (
            <CheckCircleIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-client-agreement-accepted-icon'
            />
          ) : (
            <ReportIcon
              classes={['mr-4']}
              height='23px'
              dataTestId='kyc-documents-client-agreement-not-signed-icon'
            />
          )}
          <Span
            variant='bold'
            text='Client Agreement & Self-Declaration Signed'
            classes={['ml-16']}
          />
        </Flex>

        {kycDocumentsChecklist.clientAgreementDocumentId && (
          <DownloadIcon
            height='18'
            width='18'
            classes={['pointer', 'mt-auto']}
            onClick={() =>
              handleClientAgreementDownload(
                kycDocumentsChecklist.clientAgreementDocumentId as string,
              )
            }
            dataTestId='kyc-documents-client-agreement-download-icon'
          />
        )}
      </Flex>

      {/* KYC Documents */}
      {kycDocumentsChecklist.kycStatus === 'APPROVED' && (
        <>
          <P
            text='KYC Documents'
            classes={['mt-20 mb-16']}
            dataTestId='kyc-documents-documents-section-title'
          />
          <Flex variant='space-between'>
            <Flex>
              {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'ID Proof')?.documentData ? (
                <CheckCircleIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-id-proof-verified-icon'
                />
              ) : (
                <ReportIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-id-proof-unverified-icon'
                />
              )}

              <Span variant='bold' text={'ID Proof'} classes={['ml-16']} />
            </Flex>
            {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'ID Proof')?.documentData && (
              <DownloadIcon
                height='18'
                width='18'
                classes={['pointer', 'mt-auto']}
                onClick={() =>
                  handleSumsubKYCDocumentDownload(
                    getKYCDocument(kycDocumentsChecklist.kycDocuments, 'ID Proof') ?? null,
                  )
                }
                dataTestId='kyc-documents-id-proof-download-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Flex>
              {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Address Proof')?.documentData ? (
                <CheckCircleIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-address-proof-verified-icon'
                />
              ) : (
                <ReportIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-address-proof-unverified-icon'
                />
              )}

              <Span variant='bold' text={'Address Proof'} classes={['ml-16']} />
            </Flex>

            {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Address Proof')?.documentData && (
              <DownloadIcon
                height='18'
                width='18'
                classes={['pointer', 'mt-auto']}
                onClick={() =>
                  handleSumsubKYCDocumentDownload(
                    getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Address Proof') ?? null,
                  )
                }
                dataTestId='kyc-documents-address-proof-download-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Flex>
              {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Source of Funds')
                ?.documentData ? (
                <CheckCircleIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-source-of-funds-verified-icon'
                />
              ) : (
                <ReportIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-source-of-funds-unverified-icon'
                />
              )}

              <Span variant='bold' text={'Source of Funds'} classes={['ml-16']} />
            </Flex>

            {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Source of Funds')
              ?.documentData && (
              <DownloadIcon
                height='18'
                width='18'
                classes={['pointer', 'mt-auto']}
                onClick={() =>
                  handleSumsubKYCDocumentDownload(
                    getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Source of Funds') ?? null,
                  )
                }
                dataTestId='kyc-documents-source-of-funds-download-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Flex>
              {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Curriculum Vitae')
                ?.documentData ? (
                <CheckCircleIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-curriculum-vitae-verified-icon'
                />
              ) : (
                <ReportIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='kyc-documents-curriculum-vitae-unverified-icon'
                />
              )}
              <Span variant='bold' text={'Curriculum Vitae'} classes={['ml-16']} />
            </Flex>

            {getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Curriculum Vitae')
              ?.documentData && (
              <DownloadIcon
                height='18'
                width='18'
                classes={['pointer', 'mt-auto']}
                onClick={() =>
                  handleSumsubKYCDocumentDownload(
                    getKYCDocument(kycDocumentsChecklist.kycDocuments, 'Curriculum Vitae') ?? null,
                  )
                }
                dataTestId='kyc-documents-curriculum-vitae-download-icon'
              />
            )}
          </Flex>
        </>
      )}
    </Card>
  )
}

export { KYCDocumentsPresenter }
